import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { useApi } from "../../api";
import { useAuth } from "../../auth";
import { useWhitelabel } from "../../domain/whitelabel";
import { useObjectConfig } from "../../objectConfig";
import { MAP_DEVICE_STATUSES, getMapStatus } from "../../domain/device";
import Map from "./Map";

const STATUS_FILTER_CONFIG_KEY = "ui.devices.map.statusFilter";

const MapContainer = () => {
  const api = useApi();
  const { user } = useAuth();
  const { whitelabelConfig } = useWhitelabel();
  const { get: getConfig, set: setConfig } = useObjectConfig();

  const [devices, setDevices] = useState([]);
  const [highlightedDevice, setHighlightedDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatuses, setSelectedStatuses] = useState(MAP_DEVICE_STATUSES);

  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const highlightedId = parseInt(query.get("highlight"), 10);

  const sensorSettings = useMemo(
    () => ({
      showNames: !!whitelabelConfig["ui.devices.list.sensors.showNames"],
      showVisuals: !!whitelabelConfig["ui.devices.list.sensors.showVisuals"],
      showUnits: !!whitelabelConfig["ui.devices.list.sensors.showUnits"],
      showPercentages: !!whitelabelConfig[
        "ui.devices.list.sensors.showPercentages"
      ],
      showTemperatures: !!whitelabelConfig[
        "ui.devices.list.sensors.showTemperatures"
      ]
    }),
    [whitelabelConfig]
  );

  useEffect(() => {
    api.get(`/v2/devices?location_enabled_only=true`).then(response => {
      setDevices(response.data);
      setLoading(false);

      if (highlightedId) {
        const hd = response.data.find(e => e.id === highlightedId);
        if (hd !== undefined) {
          setHighlightedDevice(hd);
        }
      }
    });
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    getConfig([
      { obj: "whitelabel", id: user.id, key: STATUS_FILTER_CONFIG_KEY }
    ])
      .then(data => {
        const saved = data[STATUS_FILTER_CONFIG_KEY];

        if (Array.isArray(saved) && saved.length > 0) {
          setSelectedStatuses(saved);
        }
      })
      .catch(() => {});
  }, [user, getConfig]);

  const persistStatusFilter = statuses => {
    setConfig("whitelabel", user.id, STATUS_FILTER_CONFIG_KEY, statuses);
  };

  const handleToggleStatus = status => {
    setSelectedStatuses(prev => {
      const next = prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status];

      persistStatusFilter(next);

      return next;
    });
  };

  const handleReset = () => {
    setSelectedStatuses(MAP_DEVICE_STATUSES);
    persistStatusFilter(MAP_DEVICE_STATUSES);
  };

  const filteredDevices = useMemo(
    () =>
      devices.filter(device => selectedStatuses.includes(getMapStatus(device))),
    [devices, selectedStatuses]
  );

  if (loading) {
    return null;
  }

  return (
    <Map
      devices={devices}
      filteredDevices={filteredDevices}
      highlightedDevice={highlightedDevice}
      sensorSettings={sensorSettings}
      selectedStatuses={selectedStatuses}
      onToggleStatus={handleToggleStatus}
      onReset={handleReset}
    />
  );
};

export default MapContainer;
